import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useBookmarkStore, useLightboxStore, useModelStore, useToastStore, useUIStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MessageContentRenderer from '@/components/Messages/MessageContentRenderer';
import MessageFiles from '@/components/Messages/MessageFiles';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { Tooltip } from '@/components/Tooltip';
import { openMessageFile } from '@/constants/utils/files';
import { buildFolderTree, flattenFolderTree } from '@/constants/utils/folder_tree';
import {
  extractMessageModelIds,
  isAutoModeAttribution,
  resolveAttributionModelIds,
} from '@/constants/utils/message';

const PREVIEW_MAX = 360;

const extractTitleAndBody = (content) => {
  if (!content) return { title: '', body: '' };
  const trimmed = content.trim();
  const firstBreak = trimmed.search(/\n{1,}/);
  const firstLine = firstBreak === -1 ? trimmed : trimmed.slice(0, firstBreak);
  const rest = firstBreak === -1 ? '' : trimmed.slice(firstBreak).trim();
  const cleanedTitle = firstLine
    .replace(/^#+\s*/, '')
    .replace(/^[-*+]\s+/, '')
    .replace(/^>\s+/, '')
    .replace(/^\*\*(.+?)\*\*$/, '$1')
    .replace(/^`(.+?)`$/, '$1')
    .trim();
  const title = cleanedTitle.length > 90
    ? cleanedTitle.slice(0, 90).trimEnd() + '…'
    : cleanedTitle;
  return { title, body: rest };
};

const getInlineSnippet = (body) => {
  if (!body) return '';
  const flat = body
    .replace(/```[\s\S]*?```/g, ' [code] ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*]\([^)]+\)/g, ' [image] ')
    .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
    .replace(/[#>*_~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return flat.length > PREVIEW_MAX ? flat.slice(0, PREVIEW_MAX).trimEnd() + '…' : flat;
};

const ActionButton = ({ icon, label, onPress, destructive, colors, styles }) => (
  <Tooltip label={label} side="top">
    <Pressable
      onPress={(e) => {
        e?.stopPropagation?.();
        onPress?.(e);
      }}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ hovered, pressed }) => [
        styles.actionButton,
        (hovered || pressed) && styles.actionButtonHovered,
        destructive && hovered && { backgroundColor: `${colors.color_destructive}14` },
      ]}
    >
      <Icon
        name={icon}
        size={15}
        color={destructive ? colors.color_destructive : colors.text_color_primary}
      />
    </Pressable>
  </Tooltip>
);

const MoveToFolderMenu = React.memo(({ folders, conversationId, messageId, colors, styles, t, containingFolderIds, onOpenChange }) => {
  const addToFolder = useBookmarkStore(state => state.addToFolder);
  const { addToast } = useToastStore(state => state.actions);

  const rows = useMemo(
    () => flattenFolderTree(buildFolderTree(folders || [])),
    [folders],
  );

  const handleAddToFolder = useCallback(async (folder) => {
    try {
      await addToFolder(folder.id, conversationId, messageId);
      addToast(t('pages.bookmarks.addedToFolder').replace('%FOLDER%', folder.name), 'success');
    } catch {
      addToast(t('pages.bookmarks.addToFolderFailed'), 'error');
    }
  }, [addToFolder, conversationId, messageId, addToast, t]);

  return (
    <DropdownMenu.Root onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger>
        <Tooltip label={t('pages.bookmarks.addToFolder')} side="top">
          <Pressable
            onPress={(e) => e.stopPropagation?.()}
            accessibilityRole="button"
            accessibilityLabel={t('pages.bookmarks.addToFolder')}
            style={({ hovered, pressed }) => [
              styles.actionButton,
              (hovered || pressed) && styles.actionButtonHovered,
            ]}
          >
            <Icon name="folder-plus" size={15} color={colors.text_color_primary} />
          </Pressable>
        </Tooltip>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="DropdownMenuContent" align="end" sideOffset={6}>
        {rows.length > 0 ? (
          rows.map((folder) => {
            const inFolder = containingFolderIds.has(String(folder.id));
            return (
              <DropdownMenu.Item
                key={folder.id}
                className="DropdownMenuItem"
                onSelect={() => handleAddToFolder(folder)}
                disabled={inFolder}
              >
                <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                  <Icon name={folder.icon || 'folder'} size={13} color={colors.text_color_primary} />
                </DropdownMenu.ItemIcon>
                <DropdownMenu.ItemTitle
                  className="DropdownMenuItemTitle"
                  style={{ paddingLeft: (folder.depth || 0) * 12 }}
                >
                  {folder.name}
                </DropdownMenu.ItemTitle>
                {inFolder ? (
                  <Icon name="check" size={12} color={colors.text_color_tertiary} />
                ) : null}
              </DropdownMenu.Item>
            );
          })
        ) : (
          <DropdownMenu.Item className="DropdownMenuItem" disabled>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('pages.bookmarks.noFolders')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

MoveToFolderMenu.displayName = 'MoveToFolderMenu';

function BookmarkCard({ item, t, onUnsave, isXS, folders, activeFolderId }) {
  const { colors, theme } = useTheme();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const dropdownOpenRef = useRef(false);

  const { message, conversationId, conversationTitle } = item;
  const isAssistant = message.role === 'assistant';
  const removeFromFolder = useBookmarkStore(state => state.removeFromFolder);
  const getModelById = useModelStore(state => state.getModelById);
  const openLightbox = useLightboxStore(state => state.actions.openLightbox);

  const hasFiles = Array.isArray(message.files) && message.files.length > 0;

  const handleFilePress = useCallback((file) => {
    openMessageFile(file, {
      onOpenLightbox: (opts) => openLightbox({ ...opts, conversationId }),
      openToEditor: false,
      withInpainting: true,
      withChatInput: true,
    });
  }, [openLightbox, conversationId]);

  const primaryModel = useMemo(() => {
    if (!isAssistant) return null;
    const isAuto = isAutoModeAttribution(null, message);
    const ids = resolveAttributionModelIds(
      { ...message, modelIds: extractMessageModelIds(message) },
      null,
      isAuto,
    );
    if (ids.length === 0) return null;
    const wanted = String(ids[0]);
    const fromMessage = (message.models || []).find((model) => {
      const id = typeof model === 'string' ? model : (model?.id || model?._id);
      return id && String(id) === wanted;
    });
    if (fromMessage?.name) return fromMessage;
    const storeModel = getModelById(wanted);
    return storeModel
      ? { id: wanted, name: storeModel.name, logoPath: storeModel.logoPath, slug: storeModel.slug }
      : (fromMessage || null);
  }, [isAssistant, message, getModelById]);

  const formattedDate = useMemo(() => {
    if (!message.date) return '';
    const d = new Date(message.date);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, [message.date]);

  const { title, body } = useMemo(() => extractTitleAndBody(message.content), [message.content]);
  const snippet = useMemo(() => {
    const fromBody = getInlineSnippet(body);
    if (fromBody) return fromBody;
    const full = getInlineSnippet(message.content || '');
    if (!full || full === title) return '';
    const titleStem = (title || '').replace(/…$/, '');
    if (titleStem && full.startsWith(titleStem)) {
      return full.slice(titleStem.length).trim();
    }
    return full;
  }, [body, message.content, title]);
  const themeObj = useMemo(() => ({ colors, theme }), [colors, theme]);

  const containingFolders = useMemo(() => {
    const convId = String(conversationId);
    const msgId = String(message.id);
    return (folders || []).filter((folder) =>
      folder.items?.some((entry) =>
        String(entry.conversationId) === convId && String(entry.messageId) === msgId
      )
    );
  }, [folders, conversationId, message.id]);

  const containingFolderIds = useMemo(
    () => new Set(containingFolders.map((folder) => String(folder.id))),
    [containingFolders],
  );

  const handleOpenChat = useCallback((e) => {
    e?.stopPropagation?.();
    if (dropdownOpenRef.current) return;
    useUIStore.getState().requestScrollToMessage({
      conversationId,
      messageId: message.id,
      viewPosition: 0,
    });
    router.push(`/c/${conversationId}`);
  }, [router, conversationId, message.id]);

  const handleToggleExpand = useCallback(() => {
    if (dropdownOpenRef.current) return;
    setExpanded((prev) => !prev);
  }, []);

  const handleUnsave = useCallback((e) => {
    e?.stopPropagation?.();
    onUnsave(conversationId, message.id);
  }, [onUnsave, conversationId, message.id]);

  const handleRemoveFromFolder = useCallback((e) => {
    e?.stopPropagation?.();
    if (activeFolderId) removeFromFolder(activeFolderId, conversationId, message.id);
  }, [activeFolderId, removeFromFolder, conversationId, message.id]);

  const handleDropdownOpenChange = useCallback((open) => {
    dropdownOpenRef.current = open;
  }, []);

  const displayTitle = title || '(No text content)';
  const otherFolders = containingFolders.filter(
    (folder) => String(folder.id) !== String(activeFolderId || ''),
  );
  const visibleFolderChips = otherFolders.slice(0, 2);
  const extraFolderCount = otherFolders.length - visibleFolderChips.length;

  const actionButtons = (
    <View style={styles.actions}>
      <MoveToFolderMenu
        folders={folders}
        conversationId={conversationId}
        messageId={message.id}
        colors={colors}
        styles={styles}
        t={t}
        containingFolderIds={containingFolderIds}
        onOpenChange={handleDropdownOpenChange}
      />
      {activeFolderId ? (
        <ActionButton
          icon="folder-minus"
          label={t('pages.bookmarks.removeFromFolder')}
          onPress={handleRemoveFromFolder}
          colors={colors}
          styles={styles}
        />
      ) : null}
      <ActionButton
        icon="arrow-up-right"
        label={t('pages.bookmarks.openChat')}
        onPress={handleOpenChat}
        colors={colors}
        styles={styles}
      />
      <ActionButton
        icon="trash"
        label={t('pages.bookmarks.unsave')}
        onPress={handleUnsave}
        destructive
        colors={colors}
        styles={styles}
      />
    </View>
  );

  return (
    <Pressable
      onPress={handleToggleExpand}
      accessibilityRole="button"
      accessibilityLabel={displayTitle}
      style={({ hovered }) => [
        styles.card,
        hovered && styles.cardHovered,
        expanded && styles.cardExpanded,
      ]}
    >
      <View style={styles.header}>
        <View style={styles.titleBlock}>
          <Icon
            name={expanded ? 'chevron-down' : 'chevron-right'}
            size={12}
            color={colors.text_color_tertiary}
            style={styles.expandIcon}
          />
          <Text size="m" weight="semibold" numberOfLines={expanded ? 4 : 2} style={styles.title}>
            {displayTitle}
          </Text>
          {!isAssistant ? (
            <View style={styles.rolePill}>
              <Text size="xxs" color="secondary" weight="semibold">YOU</Text>
            </View>
          ) : null}
        </View>
      </View>

      {hasFiles ? (
        <Pressable onPress={(e) => e.stopPropagation?.()} style={styles.filesBlock}>
          <MessageFiles
            files={message.files}
            align="left"
            isSmall={!expanded}
            isEditable={false}
            onPress={handleFilePress}
          />
        </Pressable>
      ) : null}

      {snippet && !expanded ? (
        <Text size="s" color="secondary" numberOfLines={3} style={styles.snippet}>
          {snippet}
        </Text>
      ) : null}

      {expanded && message.content ? (
        <View style={styles.expandedBlock}>
          <MessageContentRenderer
            content={message.content}
            theme={themeObj}
            isStreaming={false}
            isUser={!isAssistant}
          />
        </View>
      ) : null}

      <View style={styles.footer}>
        <View style={styles.meta}>
          <View style={styles.metaChip}>
            <Icon name="message-lines" size={12} color={colors.text_color_tertiary} />
            <Text size="xs" color="secondary" numberOfLines={1} style={styles.metaLabel}>
              {conversationTitle || 'Untitled chat'}
            </Text>
          </View>
          {primaryModel?.name ? (
            <View style={styles.metaChip}>
              {primaryModel.logoPath ? (
                <Image source={{ uri: primaryModel.logoPath }} style={styles.modelLogo} contentFit="cover" />
              ) : (
                <Icon name="sparkles" size={12} color={colors.text_color_tertiary} />
              )}
              <Text size="xs" color="secondary" numberOfLines={1}>{primaryModel.name}</Text>
            </View>
          ) : null}
          {formattedDate ? (
            <View style={styles.metaChip}>
              <Icon name="clock" size={12} color={colors.text_color_tertiary} />
              <Text size="xs" color="secondary">{formattedDate}</Text>
            </View>
          ) : null}
          {visibleFolderChips.map((folder) => (
            <View key={folder.id} style={styles.metaChip}>
              <Icon name={folder.icon || 'folder'} size={12} color={colors.text_color_tertiary} />
              <Text size="xs" color="secondary" numberOfLines={1}>{folder.name}</Text>
            </View>
          ))}
          {extraFolderCount > 0 ? (
            <Text size="xs" color="tertiary">+{extraFolderCount}</Text>
          ) : null}
        </View>
        {actionButtons}
      </View>
    </Pressable>
  );
}

const createStyles = (colors, isXS) => StyleSheet.create({
  card: {
    paddingHorizontal: isXS ? 14 : 18,
    paddingVertical: isXS ? 14 : 16,
    marginBottom: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    gap: 10,
    ...(Platform.OS === 'web'
      ? { transition: 'background-color 0.12s ease, border-color 0.12s ease', cursor: 'pointer' }
      : {}),
  },
  cardHovered: {
    backgroundColor: colors.solid_color_tertiary,
    borderColor: colors.border_color_primary,
  },
  cardExpanded: {
    backgroundColor: colors.solid_color_block,
    borderColor: colors.border_color_primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  expandIcon: {
    marginTop: 4,
    flexShrink: 0,
  },
  title: {
    flex: 1,
    minWidth: 0,
    lineHeight: 22,
  },
  rolePill: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: colors.solid_color_secondary,
    marginTop: 2,
    flexShrink: 0,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    paddingTop: 4,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border_color_secondary,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  actionButtonHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.border_color_primary,
  },
  filesBlock: {
    maxWidth: '100%',
  },
  snippet: {
    lineHeight: 21,
  },
  expandedBlock: {
    paddingTop: 4,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border_color_secondary,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 160,
  },
  metaChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    maxWidth: isXS ? 160 : 240,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
  },
  metaLabel: {
    flexShrink: 1,
  },
  modelLogo: {
    width: 14,
    height: 14,
    borderRadius: 3,
  },
});

export default React.memo(BookmarkCard);
